import { NsjAuthInstance } from '@core/authentication/nsj-autenticacao';
import { painelAdministrativoApi as api } from './painel-administrativo.api';
import {
    PainelAdministrativoContexto,
    PainelAdministrativoModulo,
    PainelAdministrativoModuloInfo
} from './painel-administrativo.modulo';
import { dicas } from './painel-administrativo.dica';
import { PAINEL_ADMINISTRATIVO_CSS } from './painel-administrativo.styles';
import { escaparHtml, mensagem } from './painel-administrativo.utils';
import { LicencasModulo } from './modulos/licencas/licencas.modulo';

/**
 * Implementações dos módulos, por código. O backend decide quais módulos existem
 * (AdminBundle\Resources\Constant\PainelAdministrativoModulosConstant); módulo sem implementação
 * aqui aparece no menu com aviso, em vez de sumir.
 */
const MODULOS: { [codigo: string]: () => PainelAdministrativoModulo } = {
    licencas: () => new LicencasModulo(),
};

const INICIO = 'inicio';
const CHAVE_MENU_RECOLHIDO = 'painelAdministrativo.menuRecolhido';

const LOGO = 'https://s3.sa-east-1.amazonaws.com/imagens.nasajon/logos/sistemas-web/versao-padrao/icone-mais-nome/icn-nome-meurh.svg';

/**
 * Painel administrativo interno da Nasajon (/painel-administrativo). Fica fora do AngularJS e do
 * escopo de tenant, como as páginas públicas, mas exige login no Keycloak; quem autoriza é o
 * backend (ROLE_INTERNO_MEURH em /interno/).
 *
 * URL: ?modulo=<codigo>&secao=<codigo>; sem módulo, página inicial.
 */
class PainelAdministrativoPage {
    private modulos: PainelAdministrativoModuloInfo[] = [];
    private instancias: { [codigo: string]: PainelAdministrativoModulo } = {};
    private moduloAtivo = INICIO;
    private secaoAtiva: string | null = null;
    /** Grupos abertos no menu (accordion). O do módulo ativo abre sozinho. */
    private gruposAbertos: { [codigo: string]: boolean } = {};

    private contexto: PainelAdministrativoContexto = {
        navegar: (modulo: string, secao?: string) => this.abrir(modulo, secao),
    };

    async mount(): Promise<void> {
        document.title = 'Painel administrativo | Meu RH';
        document.body.innerHTML = `
            <style>${PAINEL_ADMINISTRATIVO_CSS}</style>
            <div class="pa ${this.menuRecolhidoSalvo() ? 'pa--menu-recolhido' : ''}">
                <header class="pa-header">
                    <button class="pa-sanduiche" data-acao="alternar-menu" aria-label="Abrir ou fechar o menu" title="Abrir ou fechar o menu">
                        <i class="fas fa-bars"></i>
                    </button>
                    <img src="${LOGO}" alt="Meu RH">
                    <h1>Painel administrativo</h1>
                    <span class="pa-usuario"><i class="fas fa-user-circle"></i>${escaparHtml(this.usuario())}</span>
                    <button class="pa-btn pa-btn--secundario" data-acao="sair"><i class="fas fa-sign-out-alt"></i>Sair</button>
                </header>
                <div class="pa-corpo">
                    <nav class="pa-menu" data-menu></nav>
                    <main class="pa-main">
                        <h2 class="pa-titulo" data-titulo></h2>
                        <section data-modulo><div class="pa-vazio">Carregando…</div></section>
                    </main>
                </div>
            </div>
        `;

        (document.querySelector('[data-acao="sair"]') as HTMLElement).onclick = () => this.sair();
        (document.querySelector('[data-acao="alternar-menu"]') as HTMLElement).onclick = () => this.alternarMenu();
        dicas.iniciar();
        (document.querySelector('[data-menu]') as HTMLElement).onclick = (evento) => this.aoNavegar(evento);

        try {
            this.modulos = await api.get<PainelAdministrativoModuloInfo[]>('admin_painel_administrativo_index');
        } catch (e) {
            this.secao().innerHTML = mensagem(e.message, 'erro');
            return;
        }

        const params = new URLSearchParams(window.location.search);
        this.abrir(params.get('modulo') || INICIO, params.get('secao') || undefined);
    }

    private aoNavegar(evento: MouseEvent): void {
        const grupo = (evento.target as HTMLElement).closest('[data-alternar-grupo]') as HTMLElement | null;
        if (grupo) {
            const codigo = grupo.getAttribute('data-alternar-grupo');
            this.gruposAbertos[codigo] = !this.gruposAbertos[codigo];
            this.renderMenu();
            return;
        }

        const alvo = (evento.target as HTMLElement).closest('[data-ir-modulo]') as HTMLElement | null;
        if (alvo) {
            this.abrir(alvo.getAttribute('data-ir-modulo'), alvo.getAttribute('data-ir-secao') || undefined);
        }
    }

    private abrir(codigo: string, secao?: string): void {
        const info = this.modulos.find(m => m.codigo === codigo);
        const modulo = info ? this.instancia(codigo) : null;

        if (!info) {
            codigo = INICIO;
        }

        if (modulo) {
            const valida = modulo.secoes.some(s => s.codigo === secao);
            secao = valida ? secao : modulo.secoes[0].codigo;
        } else {
            secao = undefined;
        }

        this.moduloAtivo = codigo;
        this.secaoAtiva = secao || null;
        if (codigo !== INICIO) {
            this.gruposAbertos[codigo] = true;
        }
        this.renderMenu();
        this.atualizarUrl();

        const container = this.secao();
        container.onclick = null;
        container.oninput = null;
        container.onchange = null;
        container.innerHTML = '';

        if (codigo === INICIO) {
            this.titulo('fa-home', 'Página inicial');
            this.renderInicio(container);
            return;
        }

        if (!modulo) {
            this.titulo('fa-puzzle-piece', info.nome);
            container.innerHTML = mensagem(`O módulo "${codigo}" ainda não tem tela nesta versão do frontend.`, 'info');
            return;
        }

        const nomeSecao = modulo.secoes.find(s => s.codigo === secao);
        this.titulo(nomeSecao ? nomeSecao.icone : modulo.icone, nomeSecao ? nomeSecao.nome : secao, info.nome);
        modulo.montar(container, secao, this.contexto);
    }

    private renderInicio(container: HTMLElement): void {
        container.innerHTML = `
            ${this.modulos.map(m => `
                <div class="pa-secao">
                    <h2><i class="fas fa-fw ${escaparHtml(this.instancia(m.codigo) ? this.instancia(m.codigo).icone : 'fa-puzzle-piece')}"></i>${escaparHtml(m.nome)}</h2>
                    <p class="pa-ajuda">${escaparHtml(m.descricao)}</p>
                    <div data-resumo-modulo="${escaparHtml(m.codigo)}"></div>
                    <div class="pa-atalhos">
                        ${(this.instancia(m.codigo) ? this.instancia(m.codigo).secoes : []).map(s => `
                            <button class="pa-btn pa-btn--secundario" data-ir-modulo="${escaparHtml(m.codigo)}" data-ir-secao="${escaparHtml(s.codigo)}"><i class="fas ${escaparHtml(s.icone)}"></i>${escaparHtml(s.nome)}</button>
                        `).join('')}
                    </div>
                </div>
            `).join('') || mensagem('Nenhum módulo disponível.', 'info')}
        `;

        container.onclick = (evento) => this.aoNavegar(evento);

        this.modulos.forEach(m => {
            const modulo = this.instancia(m.codigo);
            const alvo = container.querySelector(`[data-resumo-modulo="${m.codigo}"]`) as HTMLElement | null;
            if (modulo && modulo.montarResumoInicial && alvo) {
                modulo.montarResumoInicial(alvo, this.contexto);
            }
        });
    }

    private renderMenu(): void {
        // O nome só vai no title com o menu recolhido (só ícones); expandido, o rótulo já está à vista.
        const recolhido = (document.querySelector('.pa') as HTMLElement).classList.contains('pa--menu-recolhido');
        const titulo = (rotulo: string) => recolhido ? `title="${escaparHtml(rotulo)}"` : '';
        const item = (icone: string, rotulo: string, modulo: string, secao: string | null, ativo: boolean, classe = '') => `
            <button class="pa-menu-item ${classe} ${ativo ? 'pa-menu-item--ativo' : ''}" ${titulo(rotulo)}
                data-ir-modulo="${escaparHtml(modulo)}" ${secao ? `data-ir-secao="${escaparHtml(secao)}"` : ''}>
                <i class="fas fa-fw ${escaparHtml(icone)}"></i><span class="pa-menu-rotulo">${escaparHtml(rotulo)}</span>
            </button>
        `;

        (document.querySelector('[data-menu]') as HTMLElement).innerHTML = `
            ${item('fa-home', 'Página inicial', INICIO, null, this.moduloAtivo === INICIO)}
            ${this.modulos.map(m => {
                const modulo = this.instancia(m.codigo);
                const ativo = this.moduloAtivo === m.codigo;

                // Módulo sem tela no frontend não tem subitens: o item leva direto ao aviso.
                if (!modulo) {
                    return item('fa-puzzle-piece', m.nome, m.codigo, null, ativo, 'pa-menu-item--grupo');
                }

                const aberto = !!this.gruposAbertos[m.codigo];

                return `
                    <div class="pa-menu-grupo ${aberto ? 'pa-menu-grupo--aberto' : ''}">
                        <button class="pa-menu-item pa-menu-item--grupo ${ativo ? 'pa-menu-item--grupo-ativo' : ''}"
                            data-alternar-grupo="${escaparHtml(m.codigo)}" aria-expanded="${aberto}" ${titulo(m.nome)}>
                            <i class="fas fa-fw ${escaparHtml(modulo.icone)}"></i>
                            <span class="pa-menu-rotulo">${escaparHtml(m.nome)}</span>
                            <i class="fas fa-chevron-right pa-menu-seta" aria-hidden="true"></i>
                        </button>
                        <div class="pa-menu-subitens">
                            ${modulo.secoes.map(s =>
                                item(s.icone, s.nome, m.codigo, s.codigo, ativo && this.secaoAtiva === s.codigo, 'pa-menu-item--sub')
                            ).join('')}
                        </div>
                    </div>
                `;
            }).join('')}
        `;
    }

    private instancia(codigo: string): PainelAdministrativoModulo | null {
        if (!this.instancias[codigo] && MODULOS[codigo]) {
            this.instancias[codigo] = MODULOS[codigo]();
        }

        return this.instancias[codigo] || null;
    }

    private atualizarUrl(): void {
        const query = this.moduloAtivo === INICIO
            ? ''
            : `?modulo=${encodeURIComponent(this.moduloAtivo)}${this.secaoAtiva ? `&secao=${encodeURIComponent(this.secaoAtiva)}` : ''}`;

        window.history.replaceState(window.history.state, document.title, `${window.location.pathname}${query}`);
    }

    private titulo(icone: string, texto: string, trilha?: string): void {
        (document.querySelector('[data-titulo]') as HTMLElement).innerHTML = `
            <i class="fas ${escaparHtml(icone)}"></i>
            ${trilha ? `<span class="pa-titulo-trilha">${escaparHtml(trilha)} ›</span>` : ''}
            <span>${escaparHtml(texto)}</span>
        `;
    }

    private secao(): HTMLElement {
        return document.querySelector('[data-modulo]') as HTMLElement;
    }

    /** Expande ou recolhe o menu (recolhido = só os ícones; em tela estreita, escondido) e lembra a escolha. */
    private alternarMenu(): void {
        const raiz = document.querySelector('.pa') as HTMLElement;
        const recolhido = raiz.classList.toggle('pa--menu-recolhido');
        this.renderMenu();
        try {
            window.localStorage.setItem(CHAVE_MENU_RECOLHIDO, recolhido ? '1' : '0');
        } catch (e) {
            // Sem acesso ao armazenamento local (aba anônima, bloqueio): o menu só não lembra a escolha.
        }
    }

    /** Começa recolhido; só abre expandido se a pessoa tiver expandido da última vez. */
    private menuRecolhidoSalvo(): boolean {
        try {
            return window.localStorage.getItem(CHAVE_MENU_RECOLHIDO) !== '0';
        } catch (e) {
            return true;
        }
    }

    /** Sai do Keycloak e volta para o painel, que pede login de novo. */
    private sair(): void {
        NsjAuthInstance.nsjAuth.logout({ redirectUri: `${window.location.origin}${window.location.pathname}` });
    }

    private usuario(): string {
        const token: any = NsjAuthInstance.nsjAuth && NsjAuthInstance.nsjAuth.tokenParsed;

        return token ? (token.email || token.preferred_username || '') : '';
    }
}

export const painelAdministrativoPage = new PainelAdministrativoPage();
